/**
 * gameSfx.ts — Procedural In-Game Sound Effects
 * TheBurntPeanut Simulator 9000
 *
 * All SFX generated via Web Audio API — no external audio files.
 * Shares the AudioContext from audio.ts.
 *
 * ═══════════════════════════════════════════════════════════════
 *  SOUND REFERENCE LIST
 * ═══════════════════════════════════════════════════════════════
 *
 *  REVOLVER (The Six Shooter):
 *    - playRevolverShot()     Sharp gunshot crack + metallic ring
 *    - playRevolverReload()   Cylinder spin + click
 *    - playRevolverEmpty()    Dry-fire click
 *
 *  ENEMY:
 *    - playEnemyHit()         Meaty thud + squelch
 *    - playEnemyDeath()       Death groan + body drop
 *    - playEnemySpawn()       Low rumble spawn-in
 *
 *  PLAYER:
 *    - playPlayerHit()        Pain grunt + screen shake cue
 *    - playPlayerDeath()      Dramatic death sting
 *    - playLevelUp()          Ascending chime fanfare
 *    - playXPPickup()         Quick sparkle ding
 *    - playGoldPickup()       Coin clink
 *
 *  FUTURE WEAPONS (not yet implemented):
 *    - Peanut Rifle:    Heavy sniper crack, long echo
 *    - Spatula Slash:   Whoosh + metallic clang
 *    - RPK Machine Gun: Rapid staccato bursts
 *    - Fireball Wand:   Whoosh + flame crackle
 *    - Viking Axe:      Heavy whoosh + thunk on return
 * ═══════════════════════════════════════════════════════════════
 */

// BETA 16.14 — Import duckBgmMuffled for one-shot events (Chick Magnet) that
// need a timed duck without an associated UI screen.
import { duckBgmMuffled, duckBgmForVoice, getVoiceGain, duckSfxForVoice } from './audio';

let ctx: AudioContext | null = null;
let sfxGain: GainNode | null = null;

/**
 * Initialize the SFX system. Must be called after audio.ts initAudio().
 * Accepts the shared AudioContext and SFX gain node.
 */
export function initGameSfx(audioCtx: AudioContext, sfxGainNode: GainNode) {
  ctx = audioCtx;
  sfxGain = sfxGainNode;
  preloadSamples();
}

// ─── Sample playback helpers ─────────────────────────────────
const bufferCache = new Map<string, AudioBuffer>();

async function loadBuffer(path: string): Promise<AudioBuffer | null> {
  if (!ctx) return null;
  if (bufferCache.has(path)) return bufferCache.get(path)!;
  try {
    const res = await fetch(path);
    const arr = await res.arrayBuffer();
    const buf = await ctx.decodeAudioData(arr);
    bufferCache.set(path, buf);
    return buf;
  } catch {
    return null;
  }
}

function playBuffer(buf: AudioBuffer, volume = 1.0) {
  if (!ctx || !sfxGain) return;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.value = volume;
  src.connect(g);
  g.connect(sfxGain);
  src.start(ctx.currentTime);
}

// Spatial playback: pans left/right and attenuates by distance automatically.
// srcX/srcY are world coordinates of the sound source.
function playBufferSpatial(buf: AudioBuffer, srcX: number, srcY: number, volume = 1.0) {
  if (!ctx || !sfxGain) return;
  const src = ctx.createBufferSource();
  src.buffer = buf;

  const panner = ctx.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 200;    // full volume within ~200 world units (about 3 player widths)
  panner.maxDistance = 1400;   // nearly silent beyond this
  panner.rolloffFactor = 2.0;  // how fast it drops off — 2 = realistic
  panner.coneInnerAngle = 360; // omnidirectional emitter
  panner.coneOuterAngle = 360;

  // Map 2D game coords to 3D audio space: X=left/right, Z=depth (game Y)
  panner.positionX.value = srcX;
  panner.positionY.value = 0;
  panner.positionZ.value = srcY;

  const g = ctx.createGain();
  g.gain.value = volume;

  src.connect(panner);
  panner.connect(g);
  g.connect(sfxGain);
  src.start(ctx.currentTime);
}

/**
 * Call every frame with the player's world position.
 * Keeps the audio listener locked to the player so spatial sounds
 * pan and attenuate correctly relative to where the player is.
 */
export function updateAudioListener(playerX: number, playerY: number) {
  if (!ctx) return;
  const l = ctx.listener;
  l.positionX.value = playerX;
  l.positionY.value = 0;
  l.positionZ.value = playerY;
  // Listener faces "into the screen" — consistent top-down perspective
  l.forwardX.value = 0; l.forwardY.value = 0; l.forwardZ.value = -1;
  l.upX.value = 0;      l.upY.value = 1;      l.upZ.value = 0;
}

// Pre-load all sample files as soon as the audio context is ready
const SAMPLES = {
  // BETA 22.87 — Perk Card System v1 kit. card_* files are the dedicated
  // ElevenLabs sounds; the Menu Feel kit (ui_confirm/ui_select) + case_jackpot
  // are the always-present fallbacks the play functions chain onto.
  cardSlam:           '/sounds/card_slam.mp3',
  cardSlamLegendary:  '/sounds/card_slam_legendary.mp3',
  cardSelect:         '/sounds/card_select.mp3',
  cardFly:            '/sounds/card_fly.mp3',
  uiConfirmFallback:  '/sounds/ui_confirm.mp3',
  uiSelectFallback:   '/sounds/ui_select.mp3',
  caseJackpotShared:  '/sounds/case_jackpot.mp3',
  revolver:           '/sounds/revolver_shot.wav',
  sniper:             '/sounds/sniper_shot.wav',
  zombieGrunt:        '/sounds/zombie_grunt.wav',
  zombieHit:          '/sounds/zombie_hit.wav',
  zombieDeath:        '/sounds/zombie_death.wav',
  hitmarker:          '/sounds/hitmarker.mp3',
  spatulaSlash:       '/sounds/spatchula-sound-1.wav',
  basketballBounce:   '/sounds/basketball_bounce.wav',
  // Item effect sounds
  gas:                '/sounds/gas.wav',
  fart:               '/sounds/fart.wav',
  tBag:               '/sounds/t_bag.wav',
  rocketSound:        '/sounds/rocket_sound.wav',
  // BETA 15.53 — wall-hop teleport jump (NOT for jump-attacks, only the
  // 2-second wall-press → teleport-over-wall trigger from 15.52).
  enemyWallJump:      '/sounds/enemy_wall_jump.wav',
  // BETA 15.68 — Dylan-provided real samples replacing procedural Web Audio synths
  lowHpHeartbeat:     '/sounds/low_hp_heartbeat.wav',
  // BETA 16.13 — Dylan-provided new samples replacing the 15.68 sounds:
  //   - level_up_v2.mp3 (replaces level_up.wav)
  //   - buncha_bullshiiii.mp3 (replaces BOTH chest_free + chest_paid — same
  //     sound now per Dylan: "free chest and regular chest are the same
  //     sound buncha bulshii")
  //   - shrine_blessing.mp3 (replaces shrine_activate.wav)
  //   - chick_magnet_activated.mp3 (NEW — Chick Magnet activation)
  levelUp:            '/sounds/level_up_v2.mp3',
  chestFreeOpen:      '/sounds/buncha_bullshiiii.mp3',   // REVERTED — Dylan's original chest sound (he loves it); the 22.15 split is undone
  chestPaidOpen:      '/sounds/buncha_bullshiiii.mp3',   // shared on purpose
  rifleReload:        '/sounds/rifle_reload.mp3',
  revolverEmpty:      '/sounds/revolver_empty.mp3',
  revolverReload:     '/sounds/revolver_reload.mp3',
  shrineActivate:     '/sounds/shrine_blessing.mp3',
  // BETA 22.93 — shrines are STONE BLOCKS (Dylan). Granite slam for the
  // choice-screen tile slam-ins; stone-mechanism grind+thunk layered under
  // the blessing at full activation. Timing rules unchanged (16.13).
  shrineStoneSlam:     '/sounds/shrine_stone_slam.mp3',
  shrineStoneActivate: '/sounds/shrine_stone_activate.mp3',
  // BETA 22.94 — the blue neon ring striking on (played with the visual
  // flicker-ignition when the claimed shrine's light switches on for good).
  shrineLightIgnite:   '/sounds/shrine_light_ignite.mp3',
  chickMagnetActivated: '/sounds/chick_magnet_activated.mp3',
  // BETA 15.74 — Audio Batch #1 from PSBOT RUN handoff (5 weapon-specific
  // fire sounds replacing the revolver_shot.wav fallback) plus Ultra Beam
  // file (special timing — see weaponAdapter for charge-up wiring)
  // plus Dylan-provided cat sounds for Ugly Kittens (spawn + attack).
  blunderbussFire:    '/sounds/blunderbuss_fire.wav',
  vikingAxeThrow:     '/sounds/viking_axe_throw.wav',
  voogooRagerThrow:   '/sounds/voogoo_rager_toss_throw.wav',
  fireballWandFire:   '/sounds/wpn_flame_jet.mp3',   // BETA 22.70 — flamethrower jet (was the old fireball fwoomph)
  terminatorFire:     '/sounds/wpn_the_terminator_fire.wav',
  ultraBeamFire:      '/sounds/ultra_beam_fire.mp3',   // BETA — new ElevenLabs blast (Dylan disliked the old wav)
  kittensSpawn:       '/sounds/kittens_spawn.wav',
  kittensAttack:      '/sounds/kittens_attack.wav',
  // BETA 15.75 — Poop Sling fire + hit (same pattern as kittens)
  poopSlingFire:      '/sounds/poop_sling_fire.mp3',
  poopSlingHit:       '/sounds/poop_sling_hit.mp3',
  // BETA 15.76 — Squirter fire (continuous water stream)
  // Squirter has no traditional reload (fires every 0.12s — 8/sec). The
  // squirter_reload.mp3 file Dylan provided is staged here but NOT yet
  // wired to a play function — pending Dylan's choice on how to handle it.
  squirterFire:       '/sounds/squirter_fire.mp3',
  squirterReload:     '/sounds/squirter_reload.mp3',
  // BETA 15.77 — Frozen Darts, Drac's Fang (melee swing), Shitty Cannon,
  // Ex Girlfriend (5 sequential overlapping shots).
  frozenDartsFire:    '/sounds/frozen_darts_fire.wav',
  dracsFangSwing:     '/sounds/dracs_fang_swing.mp3',
  shittyCannonFire:   '/sounds/shitty_cannon_fire.wav',
  exGirlfriendFire:   '/sounds/ex_girlfriend_fire.mp3',
  // BETA 15.78 — Hurray fire + Bungulator punch (item, not weapon)
  hurrayFire:         '/sounds/hurray_fire.wav',
  bungulatorPunch:    '/sounds/bungulator_punch.wav',
  // BETA — SOUND REFRESH: per-enemy death/hit (was all zombie), distinct
  // explosions (were silent / reused rocket), per-weapon impacts (ElevenLabs).
  // 23.55 — timmyDeath/timmyHit deleted with Timmy (files removed)
  eliteDeath:         '/sounds/elite_death.mp3',
  eliteHit:           '/sounds/elite_hit.mp3',
  liarNukeBlast:      '/sounds/liar_nuke_blast.mp3',
  bananaBombBurst:    '/sounds/banana_bomb_burst.mp3',
  whoopiePopBurst:    '/sounds/whoopie_pop_burst.mp3',
  morningWoodThorns:  '/sounds/morning_wood_thorns.mp3',
  // Per-weapon IMPACT sounds (files generated; wired in a follow-up batch)
  fireballWandHit:    '/sounds/wpn_flame_ignite.mp3', // BETA 22.70 — ignite crackle on flame hit
  vikingAxeHit:       '/sounds/viking_axe_hit.mp3',
  shittyCannonHit:    '/sounds/shitty_cannon_hit.mp3',
  frozenDartsHit:     '/sounds/frozen_darts_hit.mp3',
  terminatorHit:      '/sounds/terminator_hit.mp3',
  spatulaHit:         '/sounds/spatula_hit.mp3',
  ultraBeamHit:       '/sounds/ultra_beam_hit.mp3',
  karenDronesHit:     '/sounds/karen_drones_hit.mp3',
  chadAuraTick:       '/sounds/chad_aura_tick.mp3',
  // Thrown-item THROW sounds (paired with the *Burst activate sounds)
  bananaThrow:        '/sounds/banana_throw.mp3',
  whoopieThrow:       '/sounds/whoopie_throw.mp3',
  // Aura LOOP hums (continuous while the weapon is active)
  chadAuraLoop:       '/sounds/chad_aura_loop.mp3',
  karenDronesLoop:    '/sounds/karen_drones_loop.mp3',
  // Shield (BETA — swapped from Web-Audio synth to ElevenLabs files per Dylan's rule)
  shieldActivate:     '/sounds/shield_activate.mp3',
  shieldBreak:        '/sounds/shield_break.mp3',
  // Iranian Bird drone auto-fire zap (was silent)
  iranianBirdFire:    '/sounds/iranian_bird_fire.mp3',
  // BETA 22.82 — the 5 guns that fired SILENTLY (22.75 audit) + 2 bullet impacts
  smgFire:            '/sounds/wpn_smg_fire.mp3',
  rpgFire:            '/sounds/wpn_rpg_fire.mp3',
  crossbowFire:       '/sounds/wpn_crossbow_fire.mp3',
  chainWhipSwing:     '/sounds/wpn_chain_whip_swing.mp3',
  railgunFire:        '/sounds/wpn_railgun_fire.mp3',
  revolverHit:        '/sounds/revolver_hit.mp3',
  rifleHit:           '/sounds/rifle_hit.mp3',
  // BETA 22.89 — WILD CARD MACHINE + TOKENS (chests are machines now)
  machineVend:        '/sounds/machine_vend.mp3',
  tokenPickup:        '/sounds/token_pickup.mp3',
  tokenSpend:         '/sounds/token_spend.mp3',
  machineDenied:      '/sounds/machine_denied.mp3',
  // BETA 22.90 — HEART THROB perk (proximity knockback shockwave)
  heartThrobPulse:    '/sounds/heart_throb_pulse.mp3',
} as const;

export async function preloadSamples() {
  await Promise.all(Object.values(SAMPLES).map(loadBuffer));
}

// ─── Helper: distortion waveshaper curve ─────────────────────────
function makeDistortionCurve(amount: number): Float32Array {
  const n = 256;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = (Math.PI + amount) * x / (Math.PI + amount * Math.abs(x));
  }
  return curve;
}

// ─── Helper: create noise buffer ─────────────────────────────────
function noiseBuffer(duration: number, decay: number = 0.02): AudioBuffer {
  if (!ctx) throw new Error('Audio not initialized');
  const len = Math.floor(ctx.sampleRate * duration);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * decay));
  }
  return buf;
}

// ═══════════════════════════════════════════════════════════════
//  REVOLVER SOUNDS
// ═══════════════════════════════════════════════════════════════

let lastShotTime = 0;

/** Revolver shot — real sample */
export function playRevolverShot() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - lastShotTime < 0.06) return;
  lastShotTime = now;
  const buf = bufferCache.get(SAMPLES.revolver);
  if (buf) playBuffer(buf, 1.0);
}

/** BETA 16.21 — PSBOT cycle-24 P0.1: dedicated RPK shot at +5% volume.
 *  RPK was sharing playRevolverShot since 15.90 but Dylan wants RPK
 *  louder (revolver stays at 1.0). Same sample, separate gain. */
let _lastRpkShotTime = 0;
export function playRpkShot() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastRpkShotTime < 0.06) return;
  _lastRpkShotTime = now;
  const buf = bufferCache.get(SAMPLES.revolver);
  if (buf) playBuffer(buf, 1.05);
}

/** Revolver reload — real sample (BETA 15.68 — replaced procedural synth with WAV) */
export function playRevolverReload() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.revolverReload);
  if (buf) playBuffer(buf, 0.9);
}

/** Dry-fire click — empty chamber. BETA 15.68 — real sample replaces synth. */
export function playRevolverEmpty() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.revolverEmpty);
  if (buf) playBuffer(buf, 0.9);
}

// ═══════════════════════════════════════════════════════════════
//  ENEMY SOUNDS
// ═══════════════════════════════════════════════════════════════

let lastEnemyHitTime = 0;

/** Zombie grunt — hit but alive. Spatial: pans and attenuates by world position. */
export function playEnemyHit(srcX = 0, srcY = 0, type = '', variant = '', noMarker = false) {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - lastEnemyHitTime < 0.1) return;
  lastEnemyHitTime = now;
  // BETA — pick the hit sound by enemy identity (was always the zombie grunt).
  let key: string = SAMPLES.zombieGrunt;
  if (variant === 'elite') key = SAMPLES.eliteHit;   // 23.55 — timmy branch deleted with Timmy
  const grunt = bufferCache.get(key) ?? bufferCache.get(SAMPLES.zombieGrunt);
  if (grunt) playBufferSpatial(grunt, srcX, srcY, 0.6);
  // Hitmarker "ting" plays flat — skip it for aura hits (Dylan: the aura should
  // be JUST the enemy grunt, no ting — it spammed once the aura hit per-enemy).
  if (!noMarker) {
    const marker = bufferCache.get(SAMPLES.hitmarker);
    if (marker) playBuffer(marker, 0.15);
  }
}

let _deathActiveCount = 0;
const DEATH_MAX_CONCURRENT = 4;

/** Enemy death — spatial, branches by enemy type (was always zombie_death),
 *  capped at 4 simultaneous to prevent distortion (bosses bypass the cap). */
export function playEnemyDeath(srcX = 0, srcY = 0, type = '', variant = '') {
  if (!ctx || !sfxGain) return;
  // BETA — pick the death sound by enemy identity (was always zombieDeath).
  let key: string = SAMPLES.zombieDeath;
  let vol = 0.85;
  let isBig = false;
  if (variant === 'elite' || variant === 'boss') { key = SAMPLES.eliteDeath; vol = 0.9; isBig = variant === 'boss'; }   // 23.55 — timmy branch deleted with Timmy
  // Common-mob deaths are capped so a swarm doesn't distort; bosses always play.
  if (!isBig && _deathActiveCount >= DEATH_MAX_CONCURRENT) return;
  const buf = bufferCache.get(key) ?? bufferCache.get(SAMPLES.zombieDeath);
  if (!buf) return;
  _deathActiveCount++;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const panner = ctx.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 200;
  panner.maxDistance = 1400;
  panner.rolloffFactor = 2.0;
  panner.coneInnerAngle = 360;
  panner.coneOuterAngle = 360;
  panner.positionX.value = srcX;
  panner.positionY.value = 0;
  panner.positionZ.value = srcY;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(panner);
  panner.connect(g);
  g.connect(sfxGain);
  src.onended = () => { _deathActiveCount--; };
  src.start(ctx.currentTime);
}

// ═══════════════════════════════════════════════════════════════
//  EXPLOSIONS (BETA — were silent or reused the Anus Rocket sound)
// ═══════════════════════════════════════════════════════════════

/** Liar Deceiver revive NUKE — its own nuclear blast (was the rocket sound). */
export function playLiarNukeBlast(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.liarNukeBlast);
  if (buf) playBufferSpatial(buf, srcX, srcY, 1.0);
}

/** Asmon's Banana monkey-bomb trap burst (was silent). */
export function playBananaBombBurst(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.bananaBombBurst);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.85);
}

/** Whoopie Cushion freeze trap pop (was silent). */
export function playWhoopiePopBurst(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.whoopiePopBurst);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.85);
}

/** Morning Wood thorns AoE spike burst (was silent). */
export function playMorningWoodThorns(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.morningWoodThorns);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.8);
}

/** Banana monkey-bomb THROW/lob (the burst plays right after via playBananaBombBurst). */
export function playBananaThrow(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.bananaThrow);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.7);
}

/** Whoopie Cushion THROW/lob (the pop plays right after via playWhoopiePopBurst). */
export function playWhoopieThrow(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.whoopieThrow);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.7);
}

/** Iranian Bird drone auto-fire zap. Throttled — many birds fire at once and
 *  would otherwise machine-gun the sample (was completely silent before). */
let _lastIranianBirdFire = 0;
export function playIranianBirdFire(srcX = 0, srcY = 0) {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastIranianBirdFire < 0.06) return;
  _lastIranianBirdFire = now;
  const buf = bufferCache.get(SAMPLES.iranianBirdFire);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.5);
}

// BETA — per-weapon IMPACT sound. Returns TRUE if a weapon-specific impact
// played (the caller then SKIPS the generic enemy-hit grunt so they don't
// double up). Light-throttled so a rapid weapon doesn't machine-gun the sample.
let _lastWeaponHitTime = 0;
export function playWeaponHit(weaponId = '', srcX = 0, srcY = 0): boolean {
  if (!ctx || !sfxGain) return false;
  let key: string | null = null;
  switch (weaponId) {
    case 'fireball_wand':      key = SAMPLES.fireballWandHit; break;
    case 'viking_axe':         key = SAMPLES.vikingAxeHit; break;
    case 'wpn_shitty_cannon':  key = SAMPLES.shittyCannonHit; break;
    case 'wpn_frozen_darts':   key = SAMPLES.frozenDartsHit; break;
    case 'wpn_the_terminator': key = SAMPLES.terminatorHit; break;
    case 'spatula_slash':      key = SAMPLES.spatulaHit; break;
    case 'wpn_ultra_beam':     key = SAMPLES.ultraBeamHit; break;
    case 'wpn_karen_drones':   key = SAMPLES.karenDronesHit; break; // BETA 22.76 — wire the already-authored purse-smack (was a dead asset: no case → silent grunt)
    case 'revolver':           key = SAMPLES.revolverHit; break;    // BETA 22.82 — bullet-hit thud
    case 'peanut_rifle':       key = SAMPLES.rifleHit; break;       // BETA 22.82 — heavy .50-cal thud
    default: return false; // no specific impact → caller plays the enemy grunt
  }
  const now = ctx.currentTime;
  if (now - _lastWeaponHitTime < 0.05) return true; // throttled but still "handled"
  _lastWeaponHitTime = now;
  const buf = bufferCache.get(key);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.5);
  return true;
}

// ── Continuous LOOP hums (Chad Aura / Karen Drones) ──────────────────────
// One looping buffer source per loop, started when the weapon is active and
// stopped when it isn't (engine ticks this each frame). Idempotent: calling
// start while already playing is a no-op; stop is safe if nothing's playing.
const _loopSrc: Record<string, AudioBufferSourceNode | null> = {};
function _startLoop(key: string, sampleFile: string, volume: number): void {
  if (!ctx || !sfxGain || _loopSrc[key]) return;
  const buf = bufferCache.get(sampleFile);
  if (!buf) return;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const g = ctx.createGain();
  g.gain.value = volume;
  src.connect(g); g.connect(sfxGain);
  src.start();
  _loopSrc[key] = src;
}
function _stopLoop(key: string): void {
  const src = _loopSrc[key];
  if (src) { try { src.stop(); } catch { /* already stopped */ } _loopSrc[key] = null; }
}
export function startChadAuraLoop(): void { _startLoop('chad', SAMPLES.chadAuraLoop, 0.22); }
export function stopChadAuraLoop(): void { _stopLoop('chad'); }
export function startKarenDronesLoop(): void { _startLoop('karen', SAMPLES.karenDronesLoop, 0.22); }
export function stopKarenDronesLoop(): void { _stopLoop('karen'); }
/** Stop ALL weapon loops (call on run end / game over so nothing hums on the menu). */
export function stopAllWeaponLoops(): void { _stopLoop('chad'); _stopLoop('karen'); }

// ═══════════════════════════════════════════════════════════════
//  23.42 — PEANUT VOICE SYSTEM DELETED (Dylan: "delete all the peanut
//  sounds... just delete them all"). Audio is atmospheric + intentional
//  now: menu music only, in-game = SFX + world ambience. The voice-bus
//  plumbing in audio.ts stays dormant for future intentional voice use.
// ═══════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════
//  PLAYER SOUNDS
// ═══════════════════════════════════════════════════════════════

/** Player takes damage — pain grunt */
export function playPlayerHit() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Impact noise
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(0.1, 0.015);
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 400;
  noiseFilter.Q.value = 2;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.2, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(sfxGain);
  noise.start(now);

  // Low rumble
  const rumble = ctx.createOscillator();
  rumble.type = 'sine';
  rumble.frequency.value = 80;
  const rumbleGain = ctx.createGain();
  rumbleGain.gain.setValueAtTime(0.15, now);
  rumbleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  rumble.connect(rumbleGain);
  rumbleGain.connect(sfxGain);
  rumble.start(now);
  rumble.stop(now + 0.2);
}

/** XP gem pickup — quick sparkle */
export function playXPPickup() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // BETA 16.54 — Per Dylan: "gold gainina and xp audio needs to be
  // doubled." Gain envelope doubled (0.08 → 0.16 peak).
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(2400, now + 0.08);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.16, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.15);
}

/** Token pickup (BETA 22.89 — gold orbs are TOKENS now). ElevenLabs clink
 *  replaces the hand-synth coin per the all-audio-ElevenLabs rule; the old
 *  synth stays only as a first-frames fallback while the sample loads. */
export function playGoldPickup() {
  if (!ctx || !sfxGain) return;
  const tok = bufferCache.get(SAMPLES.tokenPickup);
  if (tok) { playBuffer(tok, 0.9); return; }
  const now = ctx.currentTime;

  // BETA 16.54 — Per Dylan: "gold gainina and xp audio needs to be
  // doubled." Both osc gain envelopes doubled (0.1 → 0.2 main clink,
  // 0.05 → 0.1 harmonic).
  // Coin clink
  const clink = ctx.createOscillator();
  clink.type = 'sine';
  clink.frequency.value = 3200;
  const clinkGain = ctx.createGain();
  clinkGain.gain.setValueAtTime(0.2, now);
  clinkGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  clink.connect(clinkGain);
  clinkGain.connect(sfxGain);
  clink.start(now);
  clink.stop(now + 0.12);

  // Second harmonic
  const h2 = ctx.createOscillator();
  h2.type = 'sine';
  h2.frequency.value = 4800;
  const h2Gain = ctx.createGain();
  h2Gain.gain.setValueAtTime(0.1, now + 0.02);
  h2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  h2.connect(h2Gain);
  h2Gain.connect(sfxGain);
  h2.start(now + 0.02);
  h2.stop(now + 0.1);
}

/** Level up fanfare. BETA 15.68 — real sample replaces ascending chime synth. */
export function playLevelUp() {
  if (!ctx || !sfxGain) return;
  // (Peanut's level-up line fires on the PICK — pickConfirmCelebration in engine.ts —
  //  not here, so it doesn't overlap this jingle.)
  // BETA 16.24 — PSBOT cycle-25 P0.6. Per Dylan: "ckcick magnet sound
  // should finish befor level u sound activate. because i get all gold
  // and xp so quickly you cant hear the audio from chick magnet cause
  // level up sound plays over it." If the chick magnet sound is still
  // playing, defer the level-up sound until it ends + a small gap.
  // setTimeout uses real wall-clock seconds; the chick magnet sample
  // is 1.656s so worst-case defer is ~1.7s.
  const buf = bufferCache.get(SAMPLES.levelUp);
  if (!buf) return;
  if (isChickMagnetSoundActive()) {
    const remainingMs = Math.max(50, getChickMagnetRemainingSec() * 1000 + 80);
    setTimeout(() => {
      // BETA 16.21 — PSBOT cycle-24 P0.1: +10%. 0.95 → 1.04.
      playBuffer(buf, 1.04);
    }, remainingMs);
    return;
  }
  playBuffer(buf, 1.04);
}

/** Player death sting */
export function playPlayerDeath() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Descending chromatic
  const notes = [440, 415, 392, 370, 349, 330];
  notes.forEach((freq, i) => {
    const osc = ctx!.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const g = ctx!.createGain();
    const t = now + i * 0.12;
    g.gain.setValueAtTime(0.08, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(g);
    g.connect(sfxGain!);
    osc.start(t);
    osc.stop(t + 0.2);
  });

  // Final low drone
  const drone = ctx.createOscillator();
  drone.type = 'sawtooth';
  drone.frequency.value = 55;
  const droneFilter = ctx.createBiquadFilter();
  droneFilter.type = 'lowpass';
  droneFilter.frequency.value = 200;
  const droneGain = ctx.createGain();
  droneGain.gain.setValueAtTime(0, now + 0.6);
  droneGain.gain.linearRampToValueAtTime(0.15, now + 0.8);
  droneGain.gain.exponentialRampToValueAtTime(0.001, now + 2.0);
  drone.connect(droneFilter);
  droneFilter.connect(droneGain);
  droneGain.connect(sfxGain);
  drone.start(now + 0.6);
  drone.stop(now + 2.2);
}

// ═══════════════════════════════════════════════════════════════
//  REVIVE EVENT SOUNDS — BETA 16.55 (PSBOT cycle-30m)
//  Dylan: "if sheeesh saved me i need a popup says SHEEESH with a heart
//  emoji or something and an audio that it activated."
//  Three procedural cues so revives finally have audio feedback:
//    - playSheeeshActivate    — SHEEESH! (heroic ascending sweep + bell)
//    - playLastStandActivate  — Last Stand pity-save (heart-thump x2 + brassy chime)
//    - playLiarDeceiverActivate — Liar Deceiver fakeout (descending detuned trickster)
// ═══════════════════════════════════════════════════════════════

/** SHEEESH — heroic save audio. Ascending bell + sparkle for the
 *  pink-heart popup. Loud + bright so Dylan unmistakably hears the save. */
export function playSheeeshActivate() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Major-triad ascending sweep, bell-like sine timbre with detune for shimmer
  const notes = [523.25, 659.25, 783.99, 1046.50]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const t = now + i * 0.07;
    // Main bell
    const osc = ctx!.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx!.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.22, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    osc.connect(g);
    g.connect(sfxGain!);
    osc.start(t);
    osc.stop(t + 0.6);
    // Detuned shimmer +1200 cents (octave up)
    const shimmer = ctx!.createOscillator();
    shimmer.type = 'triangle';
    shimmer.frequency.value = freq * 2;
    const sg = ctx!.createGain();
    sg.gain.setValueAtTime(0, t);
    sg.gain.linearRampToValueAtTime(0.07, t + 0.02);
    sg.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
    shimmer.connect(sg);
    sg.connect(sfxGain!);
    shimmer.start(t);
    shimmer.stop(t + 0.5);
  });

  // Final whoosh sparkle — high-frequency noise burst
  const sparkle = ctx.createBufferSource();
  sparkle.buffer = noiseBuffer(0.4, 0.02);
  const sparkleFilter = ctx.createBiquadFilter();
  sparkleFilter.type = 'highpass';
  sparkleFilter.frequency.value = 4000;
  const sparkleGain = ctx.createGain();
  sparkleGain.gain.setValueAtTime(0.0, now + 0.05);
  sparkleGain.gain.linearRampToValueAtTime(0.18, now + 0.12);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
  sparkle.connect(sparkleFilter);
  sparkleFilter.connect(sparkleGain);
  sparkleGain.connect(sfxGain);
  sparkle.start(now + 0.05);
}

/** LAST STAND — clutch save. Two heart-thumps then a brassy CLUTCH chime.
 *  Lower register than Sheeesh so Dylan can distinguish at a glance. */
export function playLastStandActivate() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Two heart-thump sub-kicks (pulse pulse), 100ms apart
  for (let i = 0; i < 2; i++) {
    const t = now + i * 0.18;
    const kick = ctx.createOscillator();
    kick.type = 'sine';
    kick.frequency.setValueAtTime(120, t);
    kick.frequency.exponentialRampToValueAtTime(45, t + 0.15);
    const kg = ctx.createGain();
    kg.gain.setValueAtTime(0.42, t);
    kg.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    kick.connect(kg);
    kg.connect(sfxGain);
    kick.start(t);
    kick.stop(t + 0.3);
  }

  // Brassy "CLUTCH!" chime — sawtooth power chord, fifth interval, briefly held
  const clutchT = now + 0.42;
  [261.63, 392.00].forEach((freq) => { // C4 + G4 power chord
    const osc = ctx!.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;
    const filter = ctx!.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1800;
    filter.Q.value = 6;
    const g = ctx!.createGain();
    g.gain.setValueAtTime(0, clutchT);
    g.gain.linearRampToValueAtTime(0.18, clutchT + 0.04);
    g.gain.linearRampToValueAtTime(0.14, clutchT + 0.25);
    g.gain.exponentialRampToValueAtTime(0.001, clutchT + 0.6);
    osc.connect(filter);
    filter.connect(g);
    g.connect(sfxGain!);
    osc.start(clutchT);
    osc.stop(clutchT + 0.65);
  });
}

/** LIAR DECEIVER — trickster fakeout. Descending detuned whistle
 *  (mocking) + warbling pitch wobble. Purple/joker vibe. */
export function playLiarDeceiverActivate() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Descending detuned whistle pair — slight pitch separation gives
  // a creepy/off-kilter feel
  const startFreq = 880;
  const endFreq = 220;
  for (let i = 0; i < 2; i++) {
    const detune = i === 0 ? 0 : -25; // cents
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.5);
    osc.detune.value = detune;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 800;
    filter.Q.value = 4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.12, now + 0.05);
    g.gain.linearRampToValueAtTime(0.10, now + 0.4);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc.connect(filter);
    filter.connect(g);
    g.connect(sfxGain);
    osc.start(now);
    osc.stop(now + 0.65);
  }

  // Warble wobble overlay — low LFO-like effect adds the trickster signature
  const wobble = ctx.createOscillator();
  wobble.type = 'triangle';
  wobble.frequency.value = 8;  // 8 Hz wobble
  const wobbleGain = ctx.createGain();
  wobbleGain.gain.value = 35;  // ±35 Hz pitch deviation
  // Modulate a sub-tone
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.value = 110;
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0, now + 0.2);
  subGain.gain.linearRampToValueAtTime(0.15, now + 0.3);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
  wobble.connect(wobbleGain);
  wobbleGain.connect(sub.frequency);  // route LFO into pitch
  sub.connect(subGain);
  subGain.connect(sfxGain);
  wobble.start(now);
  wobble.stop(now + 0.7);
  sub.start(now);
  sub.stop(now + 0.7);
}

// ═══════════════════════════════════════════════════════════════
//  PEANUT RIFLE SOUNDS
// ═══════════════════════════════════════════════════════════════

let lastRifleShotTime = 0;

/** Sniper shot — real sample */
export function playRifleShot() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - lastRifleShotTime < 0.3) return;
  lastRifleShotTime = now;
  const buf = bufferCache.get(SAMPLES.sniper);
  if (buf) playBuffer(buf, 1.0);
}

let lastSpatulaSlashTime = 0;

/** Spatula swing — real sample */
export function playSpatulaSlash() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - lastSpatulaSlashTime < 0.25) return;
  lastSpatulaSlashTime = now;
  const buf = bufferCache.get(SAMPLES.spatulaSlash);
  if (buf) playBuffer(buf, 0.85);
}

let lastBasketballTime = 0;
export function playBasketballBounce() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - lastBasketballTime < 0.18) return; // prevent rapid stacking
  lastBasketballTime = now;
  const buf = bufferCache.get(SAMPLES.basketballBounce);
  if (buf) playBuffer(buf, 0.9);
}

/** Bolt-action reload. BETA 15.68 — real sample replaces synth. */
export function playRifleReload() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.rifleReload);
  if (buf) playBuffer(buf, 0.9);
}

// ═══════════════════════════════════════════════════════════════
//  CHEST SOUNDS
// ═══════════════════════════════════════════════════════════════

/**
 * BETA 15.68 — Real chest-open samples replace procedural creak+thud+jingle.
 * Dylan provided two distinct samples: one for FREE chests (boss/elite drop,
 * no gold cost) and one for PAID chests (player spends gold to open).
 */
export function playChestOpenFree() {
  // (Peanut's loot line fires on the item PICK — pickConfirmCelebration — not here.)
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.chestFreeOpen);
  // BETA 16.33 — Dylan: "double the audio for bunch a bullshit sound"
  // 0.99 → 1.98 (literal 2× linear gain, ~+6 dB louder).
  if (buf) playBuffer(buf, 1.98);
}
export function playChestOpenPaid() {
  // (Peanut's loot line fires on the item PICK — pickConfirmCelebration — not here.)
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.chestPaidOpen);
  // BETA 16.33 — same 2× boost as the free-chest variant.
  if (buf) playBuffer(buf, 1.98);
}

// ── BETA 22.89 — WILD CARD MACHINE (chests are machines now). The old
// buncha_bullshiiii chest sample is off-theme for a vending machine; the
// machine interact paths in engine.ts call these instead. playChestOpenFree
// stays wired to the ULTRA boss chest only. ──
export function playMachineVend() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.machineVend);
  if (buf) playBuffer(buf, 1.6);
}
export function playTokenSpend() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.tokenSpend);
  if (buf) playBuffer(buf, 1.2);
}
export function playMachineDenied() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.machineDenied);
  if (buf) playBuffer(buf, 1.2);
}
/** BETA 22.90 — HEART THROB shockwave: one deep ba-bump pressure wave. */
export function playHeartThrobPulse() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.heartThrobPulse);
  if (buf) playBuffer(buf, 1.1);
}

// ── BETA 22.42 — Starforge "OPEN PC" case-crack layered SFX ────────────────────
// Dylan: "layers of sound effects ... extremely satisfying ... dopamine." The
// charge plays during the build-up; at the burst, crack + confetti + shimmer fire
// together (+ a deep jackpot boom on legendary), under his Peanut "loot" voice.
export function playCaseCharge(): void {
  loadBuffer('/sounds/case_charge.mp3').then((b) => { if (b) playBuffer(b, 0.7); });
}
export function playCaseBurst(tier: number): void {
  loadBuffer('/sounds/case_crack.mp3').then((b) => { if (b) playBuffer(b, 1.0); });
  loadBuffer('/sounds/case_confetti.mp3').then((b) => { if (b) playBuffer(b, 0.9); });
  loadBuffer('/sounds/case_shimmer.mp3').then((b) => { if (b) playBuffer(b, 0.7); });
  if (tier >= 3) loadBuffer('/sounds/case_jackpot.mp3').then((b) => { if (b) playBuffer(b, 0.95); });
}
// 23.55 — the "HOORAY!" clip (peanut_level_up_10) was deleted in the 23.42
// voice purge, so this has silently 404'd on every case crack since. Now an
// explicit no-op awaiting a NEW ElevenLabs cheer (Dylan picks the sound —
// all audio goes through generate-sfx.mjs, never hand-synthesized).
export function playCaseHurray(): void {
  // intentionally silent until the replacement cheer is generated
}
// Whoosh as the claimed mod warps into the Gear/Mods tab.
export function playCaseStash(): void {
  loadBuffer('/sounds/case_stash.mp3').then((b) => { if (b) playBuffer(b, 1.0); });
}
// ── BETA 22.21 — MODS-bench tactile SFX (Dylan: "beef all of this up, take pride;
// different awesome sounds; smoke has no sound; recycle/upgrade need their own").
// Every action is now its OWN distinct sound — nothing reused. ──
// Socket: a beefy two-stage mechanical ka-chunk.
export function playModAttach(): void {
  loadBuffer('/sounds/mod_attach.mp3').then((b) => { if (b) playBuffer(b, 1.15); });
}
// Un-socket: a lighter spring-loaded release pop.
export function playModDetach(): void {
  loadBuffer('/sounds/mod_detach.mp3').then((b) => { if (b) playBuffer(b, 1.0); });
}
// The smoke puff's voice — airy pneumatic hiss, layered UNDER attach/detach so the
// great smoke VFX finally has a sound. Played wherever the smoke puffs.
export function playModSmoke(): void {
  loadBuffer('/sounds/mod_smoke.mp3').then((b) => { if (b) playBuffer(b, 0.7); });
}
// Recycle/scrap: a crunchy industrial shred with bits clattering out — its OWN sound.
export function playModRecycle(): void {
  loadBuffer('/sounds/mod_recycle.mp3').then((b) => { if (b) playBuffer(b, 1.15); });
}
// Upgrade with scrap: a forge power-up — rising charge → anvil ring → sparkle reward.
export function playModUpgrade(): void {
  loadBuffer('/sounds/mod_upgrade.mp3').then((b) => { if (b) playBuffer(b, 1.1); });
}

// ── Extraction port (BETA 22.21) — the Tarkov-style escape hatch ──
// Port opens at 120s (inviting beacon), X arms it (urgent alarm-lock), survive 60s → success fanfare.
export function playExtractAppear(): void {
  loadBuffer('/sounds/extract_open.mp3').then((b) => { if (b) playBuffer(b, 1.0); });
}
export function playExtractArm(): void {
  loadBuffer('/sounds/extract_arm.mp3').then((b) => { if (b) playBuffer(b, 1.1); });
}
export function playExtractSuccess(): void {
  loadBuffer('/sounds/extract_success.mp3').then((b) => { if (b) playBuffer(b, 1.25); });
}

// BETA 22.98 — Cloakzy SFX deleted with the NPC (streamer likeness purge).
/**
 * BETA 15.68 — Backwards-compat wrapper. Old call sites that didn't know if
 * the chest was free or paid default to the free variant. Prefer calling
 * the explicit playChestOpenFree() / playChestOpenPaid() at the engine
 * chest-open sites where the cost is known.
 */
export function playChestOpen() {
  playChestOpenFree();
}

// ═══════════════════════════════════════════════════════════════
//  TOME / SHRINE SOUNDS
// ═══════════════════════════════════════════════════════════════

/**
 * Start a looping orbish channeling hum (player standing on a tome shrine).
 * Three detuned sines create natural beating/phasing without an LFO.
 * Returns a stop function — call it when channeling ends or completes.
 */
export function startTomeChanneling(): () => void {
  if (!ctx || !sfxGain) return () => {};
  const now = ctx.currentTime;

  const outputGain = ctx.createGain();
  outputGain.gain.setValueAtTime(0, now);
  outputGain.gain.linearRampToValueAtTime(0.10, now + 0.8);
  outputGain.connect(sfxGain);

  const oscs: OscillatorNode[] = [];

  // Three close-frequency sines create natural beating interference (the "orbish" pulse)
  [110, 110.7, 109.3].forEach(f => {
    const osc = ctx!.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = f;
    const g = ctx!.createGain();
    g.gain.value = 0.38;
    osc.connect(g); g.connect(outputGain);
    osc.start(now);
    oscs.push(osc);
  });

  // Harmonic overtone for depth
  const overtone = ctx.createOscillator();
  overtone.type = 'sine';
  overtone.frequency.value = 220.4;
  const overtoneGain = ctx.createGain();
  overtoneGain.gain.value = 0.18;
  overtone.connect(overtoneGain); overtoneGain.connect(outputGain);
  overtone.start(now);
  oscs.push(overtone);

  // Slow LFO tremolo
  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 2.5;
  const lfoDepth = ctx.createGain();
  lfoDepth.gain.value = 0.028;
  lfo.connect(lfoDepth);
  lfoDepth.connect(outputGain.gain);
  lfo.start(now);
  oscs.push(lfo);

  return () => {
    if (!ctx) return;
    const t = ctx.currentTime;
    lfoDepth.disconnect();
    outputGain.gain.cancelScheduledValues(t);
    outputGain.gain.setValueAtTime(0.10, t);
    outputGain.gain.linearRampToValueAtTime(0.001, t + 0.4);
    oscs.forEach(osc => { try { osc.stop(t + 0.45); } catch {} });
  };
}

/** Tome activation complete — rising sweep then bright major chord */
export function playTomeComplete() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // Rising sweep (magical culmination)
  const sweep = ctx.createOscillator();
  sweep.type = 'sine';
  sweep.frequency.setValueAtTime(220, now);
  sweep.frequency.exponentialRampToValueAtTime(1760, now + 0.6);
  const sweepGain = ctx.createGain();
  sweepGain.gain.setValueAtTime(0.08, now);
  sweepGain.gain.setValueAtTime(0.06, now + 0.4);
  sweepGain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
  sweep.connect(sweepGain); sweepGain.connect(sfxGain);
  sweep.start(now); sweep.stop(now + 0.75);

  // Bright major chord stagger (C5-E5-G5-C6)
  [523, 659, 784, 1047].forEach((freq, i) => {
    const t = now + 0.5 + i * 0.04;
    const osc = ctx!.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx!.createGain();
    g.gain.setValueAtTime(0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
    osc.connect(g); g.connect(sfxGain!);
    osc.start(t); osc.stop(t + 0.65);
  });

  // Sparkle tail
  const sparkle = ctx.createBufferSource();
  sparkle.buffer = noiseBuffer(0.15, 0.01);
  const sparkleFilter = ctx.createBiquadFilter();
  sparkleFilter.type = 'highpass';
  sparkleFilter.frequency.value = 6000;
  const sparkleGain = ctx.createGain();
  sparkleGain.gain.setValueAtTime(0.05, now + 0.5);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
  sparkle.connect(sparkleFilter); sparkleFilter.connect(sparkleGain); sparkleGain.connect(sfxGain);
  sparkle.start(now + 0.5);
}

/** Card hover — soft UI blip when moving mouse over level-up cards */
export function playCardHover() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(700, now);
  osc.frequency.exponentialRampToValueAtTime(1000, now + 0.05);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.04, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.1);
}

/** BETA 22.87 — Perk Card System v1: play the first cached buffer in the
 *  chain (dedicated ElevenLabs file → Menu Feel fallback). Never silent as
 *  long as the menu kit shipped. */
function _playCardChain(paths: string[], volume: number): void {
  if (!ctx || !sfxGain) return;
  for (const p of paths) {
    const buf = bufferCache.get(p);
    if (!buf) continue;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = volume;
    src.connect(g);
    g.connect(sfxGain);
    src.start();
    return;
  }
}

/** BETA 22.94 — the shrine's blue ring light STRIKES ON (one-shot, fired by
 *  the engine on the first visible frame of the ignition flicker). */
export function playShrineIgnite(): void {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.shrineLightIgnite);
  if (buf) playBuffer(buf, 0.85);
}

/** BETA 22.93 — Shrine sigil tile slam-in: a STONE slab, not a paper card.
 *  Falls back to the card slam chain if the stone sample isn't loaded. */
export function playShrineTileSlamSfx(rarity: string): void {
  _playCardChain(
    [SAMPLES.shrineStoneSlam, SAMPLES.cardSlam, SAMPLES.uiConfirmFallback],
    rarity === 'legendary' ? 0.95 : 0.75,
  );
}

/** Card slam-in impact (staggered, one per card). Legendary slams heavier. */
export function playCardSlamSfx(rarity: string): void {
  if (rarity === 'legendary') {
    _playCardChain([SAMPLES.cardSlamLegendary, SAMPLES.cardSlam, SAMPLES.uiConfirmFallback], 0.9);
  } else {
    _playCardChain([SAMPLES.cardSlam, SAMPLES.uiConfirmFallback], 0.65);
  }
}

/** Card chosen. Legendary gets the jackpot shimmer (shared with Starforge). */
export function playCardSelectSfx(rarity: string): void {
  if (rarity === 'legendary') {
    _playCardChain([SAMPLES.caseJackpotShared, SAMPLES.cardSelect, SAMPLES.uiSelectFallback], 0.9);
  } else {
    _playCardChain([SAMPLES.cardSelect, SAMPLES.uiSelectFallback], 0.8);
  }
}

/** Chosen card flying to the PERCS strip. */
export function playCardFlySfx(): void {
  _playCardChain([SAMPLES.cardFly, SAMPLES.uiSelectFallback], 0.5);
}

/** Barrel break — wood crack + low thud */
export function playBarrelBreak() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;

  // BETA 16.54 — Per Dylan: "barel smash audio ... needs to be doubled."
  // All three layers (crack 0.3 → 0.6, thud 0.28 → 0.56, rattle 0.12 →
  // 0.24) doubled.
  // High-pitched wood crack (noise burst through highpass)
  const crack = ctx.createBufferSource();
  crack.buffer = noiseBuffer(0.08, 0.008);
  const crackFilter = ctx.createBiquadFilter();
  crackFilter.type = 'highpass';
  crackFilter.frequency.value = 2000;
  const crackGain = ctx.createGain();
  crackGain.gain.setValueAtTime(0.6, now);
  crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  crack.connect(crackFilter); crackFilter.connect(crackGain); crackGain.connect(sfxGain);
  crack.start(now);

  // Low woody thud
  const thud = ctx.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(130, now + 0.02);
  thud.frequency.exponentialRampToValueAtTime(45, now + 0.18);
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(0.56, now + 0.02);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
  thud.connect(thudGain); thudGain.connect(sfxGain);
  thud.start(now + 0.02); thud.stop(now + 0.25);

  // Mid-range wood rattle
  const rattle = ctx.createBufferSource();
  rattle.buffer = noiseBuffer(0.12, 0.025);
  const rattleFilter = ctx.createBiquadFilter();
  rattleFilter.type = 'bandpass';
  rattleFilter.frequency.value = 700;
  rattleFilter.Q.value = 3;
  const rattleGain = ctx.createGain();
  rattleGain.gain.setValueAtTime(0.24, now + 0.03);
  rattleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  rattle.connect(rattleFilter); rattleFilter.connect(rattleGain); rattleGain.connect(sfxGain);
  rattle.start(now + 0.03);
}

/** Shield ACTIVATE — ElevenLabs shimmer (was Web-Audio synth; swapped per Dylan's rule: all audio = ElevenLabs). */
export function playShieldActivate() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.shieldActivate);
  if (buf) playBuffer(buf, 0.8);
}

/** Shield BREAK — ElevenLabs glassy shatter (was Web-Audio synth; swapped per Dylan's rule). */
export function playShieldBreak() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.shieldBreak);
  if (buf) playBuffer(buf, 0.85);
}

/** Health pickup — warm healing tone */
export function playHealthPickup() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  // Warm ascending tone
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(440, now);
  osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.08, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.25);
  // Soft shimmer
  const shimmer = ctx.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.value = 1320;
  const sGain = ctx.createGain();
  sGain.gain.setValueAtTime(0.04, now + 0.05);
  sGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  shimmer.connect(sGain);
  sGain.connect(sfxGain);
  shimmer.start(now + 0.05);
  shimmer.stop(now + 0.25);
}

/**
 * Low-health heartbeat warning. BETA 15.68 — real looping sample replaces
 * procedural two-thump synth. Note: the file is a "_loop" so the engine's
 * existing periodic call at HP < 25% will fire it on cadence; if you want
 * a true continuous loop, wire it through a long-lived AudioBufferSourceNode
 * with `loop = true` (deferred — current behavior matches old API).
 */
export function playLowHealthWarning() {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.lowHpHeartbeat);
  // BETA 15.92 — Amplified per Dylan: "the heartbeat low health is amplified."
  // Was 0.6; bumped to 1.0 (cap of playBuffer's volume scaling) so the
  // heartbeat carries while BGM is ducked hard via startSustainedDuck().
  // Music ducks to 15%, heartbeat fires at full SFX gain — clear separation.
  if (buf) playBuffer(buf, 1.0);
}

// ═══════════════════════════════════════════════════════════════
//  SHRINE SOUNDS (sample-based)
// ═══════════════════════════════════════════════════════════════

// 23.39 — playBossShrineActivate + shake-duration exports DELETED with the
// boss shrine (CaseOh purge). New boss arrival sound comes with Phase C.

/**
 * BETA 15.68 — Regular (perk/buff) shrine activation. Distinct from the boss
 * shrine sound. Throttled at 0.4s so rapid pickups don't stack/overlap.
 */
let _lastShrineActivateTime = 0;
export function playShrineActivate() {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastShrineActivateTime < 0.4) return;
  _lastShrineActivateTime = now;
  // BETA 22.93 — stone-mechanism grind+thunk is the PHYSICAL layer (the
  // shrine is a stone block); the blessing keeps the magical reward read
  // underneath it. Same one-shot moment as before (16.13 timing law).
  const stoneBuf = bufferCache.get(SAMPLES.shrineStoneActivate);
  if (stoneBuf) playBuffer(stoneBuf, 0.9);
  const buf = bufferCache.get(SAMPLES.shrineActivate);
  if (buf) playBuffer(buf, stoneBuf ? 0.55 : 0.85);
  // Boss shrine sample is ~9.95s. Duck the BGM for that duration so the
  // dramatic activation sound carries cleanly, then restore.
  duckBgmMuffled(9.5);
}

// BETA 17.08 cleanup — second identical `playShrineActivate` block (without
// duck call) removed; the version above with `duckBgmMuffled(9.5)` is the
// live one. Stale dev-merge duplicate.

/**
 * BETA 15.70 — Shrine sound that starts on STEP-ON (start of channeling)
 * instead of at activation completion. Per Dylan: "this is the new sound
 * that you have in game. it is the only sound for shrines. remove the old
 * one. this is the sound that should start playing the moment i step on
 * a shrine."
 *
 * Returns a stop function so shrines.ts can interrupt playback if the
 * player leaves the radius before the channel completes (matching the old
 * startTomeChanneling pattern). If the channel completes, we DON'T call
 * the stop function — the buffer source naturally plays through to the
 * end, bridging activation → buff selection UI → click → after-click.
 */
export function startShrineActivate(): () => void {
  if (!ctx || !sfxGain) return () => {};
  const buf = bufferCache.get(SAMPLES.shrineActivate);
  if (!buf) return () => {};
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.value = 0.85;
  src.connect(g);
  g.connect(sfxGain);
  src.start(ctx.currentTime);
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    try { src.stop(); } catch { /* already finished naturally */ }
  };
}

// BETA 17.08 cleanup — second identical `startShrineActivate` block removed.
// The merge of dev → main in BETA 17.07 brought in a stale duplicate from
// the 15.68 → 15.70 transition that was never cleaned up. Only one export
// is needed (kept above at line ~1002).

/** 23.39 — boss slam impact sound TOMBSTONE. The old slam sample was
 *  deleted with the CaseOh purge; Phase C roster bosses get a fresh
 *  ElevenLabs slam. Slam system still calls this so the hook stays. */
export function playBossSlam() {
  // intentionally silent until the new boss roster ships its own sample
}

/**
 * BETA 16.13 — Chick Magnet activation. Dylan-provided sample
 * (chick_magnet_activated.mp3). Plays once when player triggers a
 * Chick Magnet (vacuums all loot to player). Throttled 0.3s so rapid
 * back-to-back triggers don't stack.
 *
 * BETA 16.14 — Per Dylan: "audio ducks for chick magents sound when
 * activated. there is just no pop us screen. just ducks when the audio
 * plays for activating chick magnet." Triggers a TIMED muffled duck on
 * the BGM matching the sound's ~3.14 sec length so the chick magnet
 * sound is clearly audible, then auto-restores. No UI screen, no
 * permanent state change.
 */
let _lastChickMagnetTime = 0;
// BETA 16.24 — PSBOT cycle-25 P0.6: track when the chick magnet sound is
// actively playing so playLevelUp() (which fires on every XP-collect-
// triggered level-up — chick magnets vacuum thousands of XP at once,
// causing a cascade of level-ups whose sounds drown out the chick magnet
// stinger) can DEFER itself until the chick magnet finishes. Per Dylan:
// "ckcick magnet sound should finish befor level u sound activate."
const CHICK_MAGNET_SAMPLE_DURATION_SEC = 1.656;
let _chickMagnetEndCtxTime = 0;
export function isChickMagnetSoundActive(): boolean {
  if (!ctx) return false;
  return ctx.currentTime < _chickMagnetEndCtxTime;
}
export function getChickMagnetRemainingSec(): number {
  if (!ctx) return 0;
  return Math.max(0, _chickMagnetEndCtxTime - ctx.currentTime);
}
export function playChickMagnetActivated(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastChickMagnetTime < 0.3) return;
  _lastChickMagnetTime = now;
  _chickMagnetEndCtxTime = now + CHICK_MAGNET_SAMPLE_DURATION_SEC;
  // BETA 16.25 → 16.28: chick magnet volume escalation. PSBOT cycle-26
  // P0.3: 0.95 → 1.14 (+20%). PSBOT cycle-30c P1 / Dylan: "chick magnet
  // audio needs to be much louder. be like %50 more." 1.14 × 1.5 = 1.71.
  // Web Audio playBuffer accepts >1.0 by clamping/normalizing per-stem
  // gain. If we hit clipping at 1.71, drop to 1.5 next cycle.
  const buf = bufferCache.get(SAMPLES.chickMagnetActivated);
  if (buf) playBuffer(buf, 1.71);
  // BETA 16.16 — duck BGM (volume + lowpass) for the duration of the
  // chick magnet sound. Per Dylan: "look at how long this audio is.
  // its shorter so shorten the duck to match it." New sample is
  // 1.656 sec (was 3.14). Duck duration trimmed to 1.5s to match.
  duckBgmMuffled(1.5);
}

// BETA 17.08 cleanup — second identical `playChickMagnetActivated` (BETA
// 16.13 era, volume 0.95) removed. Newer BETA 16.28 version above (volume
// 1.71 + endCtxTime tracking) is the live one. Merge of dev → main in
// BETA 17.07 surfaced this stale duplicate.

// ═══════════════════════════════════════════════════════════════
//  ITEM EFFECT SOUNDS
// ═══════════════════════════════════════════════════════════════

/**
 * Diarrhea Gas (R11) — spawn a fart cloud.
 * Plays fart.wav + gas.wav simultaneously for maximum grossness.
 */
let _lastFartTime = 0;
export function playDiarrheaGas(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastFartTime < 0.8) return;  // don't spam farts
  _lastFartTime = now;
  const fartBuf = bufferCache.get(SAMPLES.fart);
  const gasBuf  = bufferCache.get(SAMPLES.gas);
  if (fartBuf) playBuffer(fartBuf, 0.9);
  if (gasBuf)  playBuffer(gasBuf,  0.55);  // gas under the fart, slightly quieter
}

/**
 * Gas-only effect — used by Gas Mask (R12) or any other gas-related item.
 * Just the ambient gas hiss, no fart.
 */
let _lastGasTime = 0;
export function playGasEffect(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastGasTime < 0.5) return;
  _lastGasTime = now;
  const buf = bufferCache.get(SAMPLES.gas);
  if (buf) playBuffer(buf, 0.7);
}

/**
 * Anus Rocket (R08) — rocket launches from enemy kill position.
 * Spatial sound — pans based on world position.
 */
let _lastRocketTime = 0;
export function playAnusRocket(srcX: number, srcY: number): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastRocketTime < 0.1) return;
  _lastRocketTime = now;
  const buf = bufferCache.get(SAMPLES.rocketSound);
  if (buf) playBufferSpatial(buf, srcX, srcY, 1.0);
}

// BETA 15.53 — Wall-hop teleport jump SFX. Triggered from enemySystem.ts when
// the wallPressTimer reaches 2s and the enemy teleport-jumps over a wall.
// Spatial so jumps near the player are louder. Throttled at 0.05s to prevent
// audio overlap when multiple zombies in a swarm jump simultaneously.
let _lastWallJumpTime = 0;
export function playEnemyWallJump(srcX: number, srcY: number): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastWallJumpTime < 0.05) return;
  _lastWallJumpTime = now;
  const buf = bufferCache.get(SAMPLES.enemyWallJump);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.8);
}

/**
 * Nutty T-Bag (R16) — summon entity sound.
 * Non-spatial (plays flat, entity is always near player).
 */
let _lastTBagTime = 0;
export function playTBagSummon(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastTBagTime < 0.4) return;
  _lastTBagTime = now;
  const buf = bufferCache.get(SAMPLES.tBag);
  if (buf) playBuffer(buf, 0.8);
}

// ═══════════════════════════════════════════════════════════════
//  BETA 15.74 — AUDIO BATCH #1 (PSBOT RUN handoff) + Dylan cat sounds
// ═══════════════════════════════════════════════════════════════
// Each play function is per-weapon. The fallback that played
// playRevolverShot for every gun is removed — weapons without an
// explicit sample fire silently. Same pattern as 15.69 reload.

let _lastBlunderbussFire = 0;
export function playBlunderbussFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastBlunderbussFire < 0.06) return;
  _lastBlunderbussFire = now;
  const buf = bufferCache.get(SAMPLES.blunderbussFire);
  if (buf) playBuffer(buf, 1.0);
}

let _lastVikingAxeThrow = 0;
export function playVikingAxeThrow(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastVikingAxeThrow < 0.06) return;
  _lastVikingAxeThrow = now;
  const buf = bufferCache.get(SAMPLES.vikingAxeThrow);
  if (buf) playBuffer(buf, 0.95);
}

let _lastVoogooRagerThrow = 0;
export function playVoogooRagerThrow(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastVoogooRagerThrow < 0.06) return;
  _lastVoogooRagerThrow = now;
  const buf = bufferCache.get(SAMPLES.voogooRagerThrow);
  if (buf) playBuffer(buf, 0.95);
}

let _lastFireballWandFire = 0;
export function playFireballWandFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastFireballWandFire < 0.06) return;
  _lastFireballWandFire = now;
  const buf = bufferCache.get(SAMPLES.fireballWandFire);
  if (buf) playBuffer(buf, 0.95);
}

let _lastTerminatorFire = 0;
export function playTerminatorFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastTerminatorFire < 0.06) return;
  _lastTerminatorFire = now;
  const buf = bufferCache.get(SAMPLES.terminatorFire);
  if (buf) playBuffer(buf, 0.9);
}

/**
 * BETA 15.74 — Ultra Beam fire. Audio file has a built-in 0.737s charge-up
 * before the BLAM hits. Returns the AudioBufferSourceNode so the caller
 * (weapon system) can scale playbackRate to match the current cooldown,
 * keeping the BLAM aligned with projectile spawn at any attack speed.
 *
 * Per PSBOT RUN spec: section 6 (cooldown audio sync). Default playbackRate
 * is 1.0 = audio plays in 0.737s. If the weapon's currentCooldownMs differs
 * from baseCooldownMs (737), pass that ratio as the second arg to scale.
 */
let _lastUltraBeamFire = 0;
export function playUltraBeamFire(playbackRate: number = 1.0): AudioBufferSourceNode | null {
  if (!ctx || !sfxGain) return null;
  const now = ctx.currentTime;
  if (now - _lastUltraBeamFire < 0.06) return null;
  _lastUltraBeamFire = now;
  const buf = bufferCache.get(SAMPLES.ultraBeamFire);
  if (!buf) return null;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  // BETA 20.51 — Dylan: "i just want ONE sound for the gun. the first one that
  // fires is best." The old code pitch-shifted/sped the sample by playbackRate
  // (scaled to attack speed) to keep the BLAM aligned with the charge end — but
  // stacking attack speed made it faster + higher every shot, which read as an
  // annoying charge-up whine. Always play at the natural rate so every shot is
  // the clean "first fire" sound. (playbackRate param kept for compatibility.)
  void playbackRate;
  src.playbackRate.value = 1.0;
  const g = ctx.createGain();
  g.gain.value = 1.8; // BETA 22.32 — Dylan: ultra beam was too quiet (was 0.95)
  src.connect(g);
  g.connect(sfxGain);
  // BETA 20.85 — Dylan: "starts silent for a second or two... should activate
  // ON the shot." The WAV (wpn_ultra_beam_fire.wav) has a quiet ~0.5s wind-up
  // ramp before the BLAM peaks at ~0.75s. Start playback 0.5s in so the blast
  // hits INSTANTLY on fire — no dead lead-in. (start(when, OFFSET) seeks into
  // the buffer.) Paired with the 20.85 trigger move to the actual shot moment.
  // BETA — new ElevenLabs ultra_beam_fire.mp3 hits immediately (no wind-up),
  // so play from the very start (the old wav needed a 0.5s skip).
  const _BEAM_LEADIN_SKIP = 0.0;
  src.start(now, _BEAM_LEADIN_SKIP);
  return src;
}

/**
 * BETA 15.74 — Ugly Kittens. Per Dylan-provided cat sounds:
 *   - kittens_spawn.wav: cat meowing when placed on the ground (= weapon fire)
 *   - kittens_attack.wav: cat fighting sound when stepped on (= projectile hit)
 */
let _lastKittensSpawn = 0;
export function playKittensSpawn(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastKittensSpawn < 0.08) return;
  _lastKittensSpawn = now;
  const buf = bufferCache.get(SAMPLES.kittensSpawn);
  if (buf) playBuffer(buf, 0.85);
}

let _lastKittensAttack = 0;
export function playKittensAttack(srcX: number = 0, srcY: number = 0): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  // BETA 20.52 — Dylan: the cat fight ("second") sound over-stimulated the audio
  // ("so many meows going on"). Throttle 0.15 → 0.45s (≤~2/sec instead of ~7) +
  // drop volume 0.9 → 0.6 so a screen full of cats doesn't become a meow wall.
  if (now - _lastKittensAttack < 0.45) return;
  _lastKittensAttack = now;
  const buf = bufferCache.get(SAMPLES.kittensAttack);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.6);
}

/**
 * BETA 15.75 — Poop Sling. Per Dylan-provided samples:
 *   - poop_sling_fire.mp3: shooting sound (= weapon fire)
 *   - poop_sling_hit.mp3:  hitting enemy sound (= projectile hit)
 *
 * BETA 16.10 — Per Dylan: "poop sling has two audious. the audio that
 * sounds called gotcha bitch needs to be removed and replaced with
 * whatever the poop swuish sound is for when an enmy gets hit by the
 * slingshot." The fire sample sounded like "gotcha bitch" and Dylan
 * doesn't want it. Now both fire AND hit use the SAME hit sample
 * (poop_sling_hit.mp3 — the swoosh). The fire .mp3 is left on disk in
 * case Dylan wants to revert; just unused at runtime.
 */
// BETA 21.89 — Dylan: "poop sling audio that says 'gotcha bitch' needs to be deleted."
// whisper transcription proved the 16.10 changelog had the two files BACKWARDS:
//   poop_sling_fire.mp3 = "YEET!"            (clean — keep this for the shot)
//   poop_sling_hit.mp3  = "Pfft, gotcha bitch!"  (the line to kill)
// 16.10 pointed BOTH events at poopSlingHit → so the sling said "gotcha bitch" every shot.
// Now: FIRE plays the clean "YEET" sample; HIT is silent (Dylan also wanted no hit sound).
let _lastPoopSlingFire = 0;
export function playPoopSlingFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastPoopSlingFire < 0.06) return;
  _lastPoopSlingFire = now;
  const buf = bufferCache.get(SAMPLES.poopSlingFire);   // "YEET" — NOT the gotcha-bitch line
  if (buf) playBuffer(buf, 0.95);
}

export function playPoopSlingHit(_srcX: number = 0, _srcY: number = 0): void {
  // Silenced — the only hit sample was the "gotcha bitch" voice line (deleted from runtime).
  // Kept as a no-op so existing call sites stay valid.
}

/**
 * BETA 15.76 — Squirter fire. Continuous water-stream weapon firing every
 * 0.12s. Throttle 0.06s (matches minReloadTime floor) so even at max attack
 * speed each shot plays cleanly without extreme stacking. The reload sound
 * is intentionally NOT wired — squirter has no real magazine cycle.
 */
let _lastSquirterFire = 0;
export function playSquirterFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastSquirterFire < 0.06) return;
  _lastSquirterFire = now;
  const buf = bufferCache.get(SAMPLES.squirterFire);
  if (buf) playBuffer(buf, 0.85);
}

/**
 * BETA 15.76 — Squirter reload. STAGED but not yet wired (pending Dylan's
 * choice on how to handle the squirter's lack of a traditional magazine
 * cycle). Available for the Sound Inspector to audition the file.
 */
export function playSquirterReload(): void {
  if (!ctx || !sfxGain) return;
  const buf = bufferCache.get(SAMPLES.squirterReload);
  if (buf) playBuffer(buf, 0.9);
}

// BETA 15.77 — Frozen Darts, Drac's Fang (melee swing), Shitty Cannon,
// and Ex Girlfriend (5 overlapping shots back-to-back).

let _lastFrozenDartsFire = 0;
export function playFrozenDartsFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastFrozenDartsFire < 0.04) return; // tight throttle (fast-fire weapon)
  _lastFrozenDartsFire = now;
  const buf = bufferCache.get(SAMPLES.frozenDartsFire);
  if (buf) playBuffer(buf, 0.9);
}

let _lastDracsFangSwing = 0;
export function playDracsFangSwing(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastDracsFangSwing < 0.15) return;
  _lastDracsFangSwing = now;
  const buf = bufferCache.get(SAMPLES.dracsFangSwing);
  if (buf) playBuffer(buf, 0.9);
}

let _lastShittyCannonFire = 0;
export function playShittyCannonFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastShittyCannonFire < 0.06) return;
  _lastShittyCannonFire = now;
  const buf = bufferCache.get(SAMPLES.shittyCannonFire);
  if (buf) playBuffer(buf, 0.9);
}

/**
 * BETA 15.77 — Ex Girlfriend. Per Dylan: "the gun should shoot 1,2,3,4,5
 * pretty quickly back to back. each shot using that sound for the gun. but
 * the bullet sounds do not cut the other sounds off."
 *
 * Two pieces deliver this:
 *   1. Weapon's burstDelay bumped from 0 to 0.08s so projectiles fire
 *      sequentially (not as one simultaneous fan).
 *   2. This play function uses a NEW BufferSourceNode per call (Web Audio
 *      naturally overlaps multiple sources without truncating). Throttle
 *      is just 0.02s — well under the 0.08s burstDelay so all 5 shots play.
 */
let _lastExGirlfriendFire = 0;
export function playExGirlfriendFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastExGirlfriendFire < 0.02) return;
  _lastExGirlfriendFire = now;
  const buf = bufferCache.get(SAMPLES.exGirlfriendFire);
  // BETA 16.21 — PSBOT cycle-24 P0.1: -15% volume (Dylan: too loud).
  // Was 0.9 → 0.77.
  if (buf) playBuffer(buf, 0.77);
}

// BETA 15.78 — Hurray weapon fire + Bungulator punch (item).

let _lastHurrayFire = 0;
export function playHurrayFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastHurrayFire < 0.06) return;
  _lastHurrayFire = now;
  const buf = bufferCache.get(SAMPLES.hurrayFire);
  if (buf) playBuffer(buf, 0.9);
}

// ── BETA 22.82 — the 5 guns that fired SILENTLY (22.75 weapon audit). Throttles
// match each weapon's cadence so rapid fire doesn't smear into noise.
let _lastSmgFire = 0;
export function playSmgFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastSmgFire < 0.03) return; // fast auto — tight throttle
  _lastSmgFire = now;
  const buf = bufferCache.get(SAMPLES.smgFire);
  if (buf) playBuffer(buf, 0.85);
}
let _lastRpgFire = 0;
export function playRpgFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastRpgFire < 0.12) return;
  _lastRpgFire = now;
  const buf = bufferCache.get(SAMPLES.rpgFire);
  if (buf) playBuffer(buf, 0.95);
}
let _lastCrossbowFire = 0;
export function playCrossbowFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastCrossbowFire < 0.08) return;
  _lastCrossbowFire = now;
  const buf = bufferCache.get(SAMPLES.crossbowFire);
  if (buf) playBuffer(buf, 0.9);
}
let _lastChainWhipSwing = 0;
export function playChainWhipSwing(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastChainWhipSwing < 0.12) return; // melee arc cadence
  _lastChainWhipSwing = now;
  const buf = bufferCache.get(SAMPLES.chainWhipSwing);
  if (buf) playBuffer(buf, 0.9);
}
let _lastRailgunFire = 0;
export function playRailgunFire(): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastRailgunFire < 0.2) return; // slow charged weapon
  _lastRailgunFire = now;
  const buf = bufferCache.get(SAMPLES.railgunFire);
  if (buf) playBuffer(buf, 0.95);
}

/**
 * BETA 15.78 — Bungulator punch (R01 item minion attack). Spatial.
 * Throttled at 0.12s so multiple bungulators (up to ~6 stacks + perk
 * grants) punching simultaneously don't stack-overlap into noise.
 */
let _lastBungulatorPunch = 0;
export function playBungulatorPunch(srcX: number = 0, srcY: number = 0): void {
  if (!ctx || !sfxGain) return;
  const now = ctx.currentTime;
  if (now - _lastBungulatorPunch < 0.12) return;
  _lastBungulatorPunch = now;
  const buf = bufferCache.get(SAMPLES.bungulatorPunch);
  if (buf) playBufferSpatial(buf, srcX, srcY, 0.9);
}
